// src/components/common/MarkdownRenderer.js — modern AI-content renderer.
//
// Plugin stack (the de-facto choice in 2026 AI apps — same as Cursor, Claude.ai web,
// v0, etc):
//   - remark-gfm:   tables, strikethrough, task lists, autolinks (fixes the
//                   "table renders as raw pipes" bug)
//   - remark-math:  $inline$ and $$block$$ LaTeX
//   - rehype-katex: render the parsed math
//
// Component overrides keep the calm/codex aesthetic: single accent color, no
// bright blue link colors, no decorative box-shadows. See memory/ui-design-system.md.
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { tomorrow } from 'react-syntax-highlighter/dist/esm/styles/prism';
import 'katex/dist/katex.min.css';

const REMARK_PLUGINS = [remarkGfm, remarkMath];
const REHYPE_PLUGINS = [rehypeKatex];

export default function MarkdownRenderer({ content, className = '' }) {
  return (
    <div className={`markdown-content ${className}`}>
      <ReactMarkdown
        remarkPlugins={REMARK_PLUGINS}
        rehypePlugins={REHYPE_PLUGINS}
        components={{
          code({ node, inline, className, children, ...props }) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : 'text';
            return !inline && match ? (
              <SyntaxHighlighter
                style={tomorrow}
                language={language}
                PreTag="div"
                className="code-block"
                customStyle={{
                  margin: '1rem 0',
                  borderRadius: '0.5rem',
                  fontSize: '0.875rem',
                  lineHeight: '1.5',
                  maxWidth: '100%',
                  overflowX: 'auto',
                }}
                wrapLongLines
                {...props}
              >
                {String(children).replace(/\n$/, '')}
              </SyntaxHighlighter>
            ) : (
              <code
                className="px-1 py-0.5 rounded bg-surface-subtle text-read text-[0.85em] font-mono"
                {...props}
              >
                {children}
              </code>
            );
          },

          p: ({ children }) => (
            <p className="mb-4 leading-relaxed text-read">{children}</p>
          ),

          h1: ({ children }) => (
            <h1 className="text-2xl font-semibold tracking-tight mt-6 mb-4 text-ink">{children}</h1>
          ),
          h2: ({ children }) => (
            <h2 className="text-xl font-semibold tracking-tight mt-5 mb-3 text-ink">{children}</h2>
          ),
          h3: ({ children }) => (
            <h3 className="text-base font-semibold mt-4 mb-2 text-ink">{children}</h3>
          ),

          ul: ({ children }) => <ul className="list-disc pl-5 mb-4 space-y-1 text-read">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 mb-4 space-y-1 text-read">{children}</ol>,
          li: ({ children }) => <li className="leading-relaxed">{children}</li>,

          // GFM task lists (- [ ] / - [x]) — rendered as inline checkboxes by default.
          // Just style the parent <li> to remove the bullet so the box reads cleanly.
          input: ({ type, checked, ...props }) =>
            type === 'checkbox' ? (
              <input
                type="checkbox"
                checked={checked}
                readOnly
                className="mr-2 align-middle accent-accent"
                {...props}
              />
            ) : (
              <input type={type} {...props} />
            ),

          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-line pl-4 my-4 text-mutedink italic">
              {children}
            </blockquote>
          ),

          // GFM tables. Wrap in a horizontal-scroll container so wide tables
          // don't blow up the layout on narrow viewports.
          table: ({ children }) => (
            <div className="overflow-x-auto my-4 rounded-lg border border-line">
              <table className="min-w-full text-sm">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-surface-muted">{children}</thead>,
          tr: ({ children }) => <tr className="border-b border-line last:border-b-0">{children}</tr>,
          th: ({ children }) => (
            <th className="px-3 py-2 text-left font-medium text-read">{children}</th>
          ),
          td: ({ children }) => <td className="px-3 py-2 text-read">{children}</td>,

          // Strikethrough (GFM ~~foo~~) — style as muted, not red.
          del: ({ children }) => <del className="text-faint">{children}</del>,

          a: ({ children, href }) => (
            <a
              href={href}
              className="text-accent hover:text-accent-hover underline underline-offset-2"
              target="_blank"
              rel="noopener noreferrer"
            >
              {children}
            </a>
          ),

          hr: () => <hr className="my-6 border-line" />,
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
